/**
 * ViewerModalFrame — Shared modal wrapper following the Settings modal design system.
 *
 * All viewer modals (Parts, Tools, Tutorial, Help, Share) use this frame
 * for consistent styling, animation, portal rendering, and theme token support.
 *
 * Design tokens are configurable via the Viewer Configuration Panel and flow
 * from the brand config through the parent component.
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export interface ViewerModalTheme {
  backgroundColor?: string;
  backdropBlur?: string;
  borderColor?: string;
  boxShadow?: string;
  primaryTextColor?: string;
  secondaryTextColor?: string;
  accentColor?: string;
  fontFamily?: string;
}

interface ViewerModalFrameProps {
  open: boolean;
  onClose: () => void;
  title: string;
  titleIcon?: string;
  theme?: ViewerModalTheme;
  children: React.ReactNode;
  ariaLabel?: string;
}

const DEFAULT_THEME: Required<ViewerModalTheme> = {
  backgroundColor: 'rgba(255, 255, 255, 0.04)',
  backdropBlur: '40px',
  borderColor: 'rgba(255, 255, 255, 0.1)',
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
  primaryTextColor: 'rgba(255, 255, 255, 0.95)',
  secondaryTextColor: 'rgba(255, 255, 255, 0.7)',
  accentColor: 'rgb(56, 189, 248)',
  fontFamily: 'Arial, sans-serif',
};

export function ViewerModalFrame({
  open,
  onClose,
  title,
  titleIcon,
  theme: themeProp,
  children,
  ariaLabel,
}: ViewerModalFrameProps) {
  const theme = { ...DEFAULT_THEME, ...themeProp };

  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      const timer = window.setTimeout(() => setIsVisible(true), 10);
      return () => window.clearTimeout(timer);
    }
    setIsVisible(false);
    const timer = window.setTimeout(() => setShouldRender(false), 300);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  if (!shouldRender) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 250,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(15, 17, 21, 0.6)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 0.25s ease-in-out',
        fontFamily: theme.fontFamily,
        padding: '1.5rem',
      }}
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel ?? title}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(520px, 90vw)',
          maxHeight: '85vh',
          borderRadius: '2rem',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          backgroundColor: theme.backgroundColor,
          backdropFilter: `blur(${theme.backdropBlur})`,
          WebkitBackdropFilter: `blur(${theme.backdropBlur})`,
          border: `1px solid ${theme.borderColor}`,
          boxShadow: theme.boxShadow,
          transform: isVisible ? 'scale(1)' : 'scale(0.95)',
          transition: 'transform 0.25s ease-in-out',
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: '1.5rem',
            right: '1.5rem',
            width: '2.5rem',
            height: '2.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '9999px',
            background: 'transparent',
            border: 'none',
            color: theme.secondaryTextColor,
            cursor: 'pointer',
            transition: 'all 0.3s',
            zIndex: 10,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.color = theme.primaryTextColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = theme.secondaryTextColor;
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '1.25rem', fontWeight: 300 }}>
            close
          </span>
        </button>

        {/* Header */}
        <div style={{ padding: '1.5rem 2rem 1rem 2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {titleIcon && (
              <span
                className="material-symbols-outlined"
                style={{ color: theme.accentColor, fontSize: '1.25rem', fontWeight: 300 }}
              >
                {titleIcon}
              </span>
            )}
            <h2
              style={{
                margin: 0,
                fontSize: '1.1rem',
                fontWeight: 700,
                letterSpacing: '0.025em',
                color: theme.primaryTextColor,
              }}
            >
              {title}
            </h2>
          </div>
        </div>

        {/* Content */}
        <div
          style={{
            padding: '0 2rem 1.5rem 2rem',
            overflowY: 'auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem',
          }}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * ViewerModalSection — A grouped section within a viewer modal.
 * Matches the Settings modal section card pattern.
 */
export function ViewerModalSection({
  title,
  theme,
  children,
}: {
  title?: string;
  theme?: ViewerModalTheme;
  children: React.ReactNode;
}) {
  const t = { ...DEFAULT_THEME, ...theme };

  return (
    <section>
      {title && (
        <h3
          style={{
            margin: '0 0 0.75rem 0',
            fontSize: '0.8rem',
            fontWeight: 600,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: t.secondaryTextColor,
          }}
        >
          {title}
        </h3>
      )}
      <div
        style={{
          padding: '1rem',
          borderRadius: '1rem',
          background: 'rgba(255, 255, 255, 0.02)',
          border: `1px solid ${t.borderColor}`,
        }}
      >
        {children}
      </div>
    </section>
  );
}
